import { createRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Modal } from "@/components/application/modals/modal-controlled";
import { useToast } from "@/components/application/notifications/toast-provider";
import { apiCall, ApiError } from "../../lib/apiClient";
import { useTenantList } from "../../hooks/useTenantQueries";
import {
  INVITABLE_ROLES,
  INVITABLE_ROLE_LABELS,
  toUserInviteApiPayload,
  USER_INVITATION_STATUS_LABELS,
  userInviteSchema,
  type UserInvitationRow,
  type UserInviteInput,
} from "../../schemas/userInvite";

/**
 * Admin sekce „Pozvánky" (V069) — admin pozve uživatele (Jméno, Příjmení,
 * e-mail, role) → odešle se onboarding e-mail s linkem. Pod formulářem seznam
 * čekajících pozvánek s možností zrušení.
 */

function UserInvitesPage() {
  const [rows, setRows] = useState<UserInvitationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const tenants = useTenantList();

  const form = useForm<UserInviteInput>({
    resolver: zodResolver(userInviteSchema),
    defaultValues: { firstName: "", lastName: "", email: "", role: "endUser", tenantId: "" },
    mode: "onBlur",
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiCall<{ rows: UserInvitationRow[] }>("/api/admin/user-invitations");
      setRows(res.rows);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Načtení pozvánek selhalo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  function openInvite() {
    form.reset({ firstName: "", lastName: "", email: "", role: "endUser", tenantId: "" });
    setSubmitError(null);
    setInviteOpen(true);
  }

  async function onSubmit(values: UserInviteInput) {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiCall("/api/admin/user-invitations", {
        method: "POST",
        body: JSON.stringify(toUserInviteApiPayload(values)),
      });
      toast.push({ variant: "success", message: `Pozvánka odeslána na ${values.email}.` });
      setInviteOpen(false);
      await fetchAll();
    } catch (e) {
      if (e instanceof ApiError && e.errorCode === "USER_INVITATION_DUPLICATE_PENDING") {
        setSubmitError("Pro tento e-mail už existuje aktivní pozvánka.");
      } else if (e instanceof ApiError && e.errorCode === "USER_INVITATION_USER_EXISTS") {
        setSubmitError("Uživatel s tímto e-mailem už existuje.");
      } else {
        setSubmitError(e instanceof Error ? e.message : "Odeslání pozvánky selhalo.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const revoke = useCallback(
    async (row: UserInvitationRow) => {
      try {
        await apiCall(`/api/admin/user-invitations/${row.id}/revoke`, { method: "POST" });
        toast.push({ variant: "success", message: `Pozvánka pro ${row.email} zrušena.` });
        await fetchAll();
      } catch (e) {
        toast.push({ variant: "error", message: e instanceof Error ? e.message : "Zrušení selhalo." });
      }
    },
    [fetchAll, toast],
  );

  const columns = useMemo<Column<UserInvitationRow>[]>(
    () => [
      {
        key: "name",
        header: "Jméno",
        cell: (r) => (
          <div className="flex flex-col gap-0.5">
            <strong className="text-primary">
              {r.first_name} {r.last_name}
            </strong>
            <span className="text-xs text-tertiary">{r.email}</span>
          </div>
        ),
      },
      {
        key: "role",
        header: "Role",
        cell: (r) => <Badge color="blue">{INVITABLE_ROLE_LABELS[r.role] ?? r.role}</Badge>,
      },
      {
        key: "status",
        header: "Stav",
        cell: (r) => {
          const label = USER_INVITATION_STATUS_LABELS[r.status] ?? r.status;
          if (r.status === "onboarded") {
            return (
              <span className="flex flex-col gap-0.5">
                <BadgeWithDot color="success">
                  {label}
                </BadgeWithDot>
                {r.onboarded_at && (
                  <span className="text-xs text-tertiary">
                    {new Date(r.onboarded_at).toLocaleString("cs-CZ", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                )}
              </span>
            );
          }
          if (r.status === "pending") {
            return (
              <span className="flex flex-col gap-0.5">
                <BadgeWithDot color="warning">
                  {label}
                </BadgeWithDot>
                <span className="text-xs text-tertiary">
                  do {new Date(r.expires_at).toLocaleDateString("cs-CZ", { dateStyle: "medium" })}
                </span>
              </span>
            );
          }
          return <Badge color="gray">{label}</Badge>;
        },
      },
      {
        key: "actions",
        header: "Akce",
        cell: (r) =>
          r.status === "pending" ? (
            <Button size="sm" color="secondary" onPress={() => void revoke(r)}>
              Zrušit
            </Button>
          ) : (
            <span className="text-quaternary">—</span>
          ),
      },
    ],
    [revoke],
  );

  return (
    <section aria-labelledby="invites-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 id="invites-title" className="text-2xl font-bold text-primary m-0">
            Pozvánky
          </h2>
          <p className="text-base text-tertiary m-0">
            Pozvěte uživatele e-mailem — po nastavení hesla a souhlasu se vytvoří účet.
          </p>
        </div>
        <Button color="primary" onPress={openInvite} data-testid="open-invite">
          Pozvat uživatele
        </Button>
      </header>

      {loadError && (
        <InlineAlert variant="error" title="Načtení selhalo">
          {loadError}
        </InlineAlert>
      )}

      {loading ? (
        <LoadingIndicator />
      ) : rows.length === 0 ? (
        <InlineAlert variant="info" title="Žádné pozvánky">
          Zatím žádné pozvánky. Klikněte na „Pozvat uživatele".
        </InlineAlert>
      ) : (
        <DataTable
          caption="Pozvánky"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
        />
      )}

      <Modal
        open={inviteOpen}
        title="Pozvat uživatele"
        onClose={() => setInviteOpen(false)}
        footer={
          <>
            <Button color="secondary" onPress={() => setInviteOpen(false)} isDisabled={submitting}>
              Zrušit
            </Button>
            <Button
              color="primary"
              onPress={() => void form.handleSubmit(onSubmit)()}
              isDisabled={submitting}
              isLoading={submitting}
              data-testid="invite-submit"
            >
              {submitting ? "Odesílám…" : "Odeslat pozvánku"}
            </Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
          <Controller
            control={form.control}
            name="firstName"
            render={({ field, fieldState }) => (
              <Input
                label="Jméno"
                isRequired
                type="text"
                autoComplete="given-name"
                isInvalid={Boolean(fieldState.error)}
                hint={fieldState.error?.message ?? undefined}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                data-testid="invite-first-name"
              />
            )}
          />
          <Controller
            control={form.control}
            name="lastName"
            render={({ field, fieldState }) => (
              <Input
                label="Příjmení"
                isRequired
                type="text"
                autoComplete="family-name"
                isInvalid={Boolean(fieldState.error)}
                hint={fieldState.error?.message ?? undefined}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                data-testid="invite-last-name"
              />
            )}
          />
          <Controller
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <Input
                label="E-mail"
                isRequired
                type="email"
                autoComplete="email"
                isInvalid={Boolean(fieldState.error)}
                hint={fieldState.error?.message ?? undefined}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                data-testid="invite-email"
              />
            )}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invite-role-select" className="text-sm font-medium text-secondary">
              Role <span className="text-brand-tertiary">*</span>
            </label>
            <select
              id="invite-role-select"
              className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
              {...form.register("role")}
              data-testid="invite-role"
            >
              {INVITABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {INVITABLE_ROLE_LABELS[r]}
                </option>
              ))}
            </select>
            {form.formState.errors.role?.message && (
              <p className="text-sm text-error-primary">{form.formState.errors.role.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invite-tenant-select" className="text-sm font-medium text-secondary">
              Tenant
            </label>
            <select
              id="invite-tenant-select"
              className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
              {...form.register("tenantId")}
              data-testid="invite-tenant"
            >
              <option value="">— výchozí (interní) —</option>
              {(tenants.data?.rows ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.display_name}
                </option>
              ))}
            </select>
            <p className="text-sm text-tertiary">
              Pro pozvání admina přefakturačního subjektu (např. A&L soft) zvolte jeho tenanta.
            </p>
          </div>
          {submitError && (
            <InlineAlert variant="error" title="Chyba">
              {submitError}
            </InlineAlert>
          )}
        </form>
      </Modal>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/user-invites",
  component: UserInvitesPage,
});
